// Under a coach's words: its cards (one small renderer per kind), what it read
// (tap to open) and the trail of how it got there. v2/replyExtras.js reads them;
// anything absent or malformed draws nothing.
import React, { useState } from 'react';
import { Activity, CalendarDays, ChevronDown, ChevronRight, Dumbbell, Flag, Timer } from 'lucide-react';
import { Button, FOCUS } from '../ds';
import { cn } from '../../lib/utils';
import { commands } from '../../v2/commands';
import { confirmView, factsLine, readCards, readSources, readSteps } from '../../v2/replyExtras';
import { CardActions, CardTitle, CoachCard } from './coachCard';

const SOURCE_ICON = { activity: Activity, session: CalendarDays, race: Flag };

/** A card that opens its object: the whole card is the button. */
function ObjectCard({ icon: Icon, title, facts, detail, onOpen }) {
  const body = (<>
    <Icon size={16} className="mt-0.5 shrink-0 text-accent-default" aria-hidden="true" />
    <span className="min-w-0 flex-1">
      <span className="block truncate type-label text-text-primary">{title}</span>
      {facts && <span className="block type-caption text-text-tertiary">{facts}</span>}
      {detail && <span className="mt-0.5 block type-body-small text-text-body">{detail}</span>}
    </span>
  </>);
  const frame = 'flex w-full items-start gap-2.5 rounded-2xl border border-border-subtle bg-bg-surface p-3 text-left';
  if (!onOpen) return <div className={frame}>{body}</div>;
  return <button type="button" onClick={onOpen} className={cn(frame, 'transition-colors hover:border-border-strong', FOCUS)}>{body}</button>;
}

const SessionCard = ({ card, onOpen }) => (
  <ObjectCard icon={CalendarDays} title={card.title} facts={factsLine(card)} detail={card.detail}
    onOpen={onOpen && (() => onOpen({ kind: 'session', id: card.id }))} />
);

const ActivityCard = ({ card, onOpen }) => (
  <ObjectCard icon={Activity} title={card.title} facts={factsLine(card)} detail={card.detail}
    onOpen={onOpen && (() => onOpen({ kind: 'activity', id: card.id }))} />
);

const WorkoutCard = ({ card, onOpen }) => (
  <ObjectCard icon={Dumbbell} title={card.title} facts={factsLine(card)} detail={card.detail}
    onOpen={onOpen && (() => onOpen({ kind: 'workout', id: card.id }))} />
);

function PredictionCard({ card, onOpen }) {
  const open = onOpen && card.race_id != null ? () => onOpen({ kind: 'race', id: card.race_id }) : null;
  return (
    <CoachCard>
      <p className="m-0 flex items-center gap-1.5 type-caption text-text-tertiary"><Timer size={13} aria-hidden="true" /> {card.title}</p>
      <p className="m-0 text-[22px] font-semibold leading-tight tabular-nums text-text-primary">{card.value}</p>
      {card.rows.length > 0 && (
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 type-body-small">
          {card.rows.map((r) => (
            <React.Fragment key={r.label}>
              <dt className="text-text-tertiary">{r.label}</dt>
              <dd className="m-0 tabular-nums text-text-body">{r.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {card.detail && <p className="m-0 type-body-small text-text-body">{card.detail}</p>}
      {open && <button type="button" onClick={open} className="self-start type-label text-accent-default hover:text-accent-hover">Open race</button>}
    </CoachCard>
  );
}

/** The write the coach is asking to make: the proposal's own accept, nothing else applies it. */
function ConfirmCard({ card, onAct, thread, pending }) {
  const view = confirmView(card, { thread, pending });
  const proposal = { id: card.proposal_id };
  return (
    <CoachCard tone="accent">
      <CardTitle>{card.title}</CardTitle>
      {card.text && <p className="m-0 type-body-small text-text-body">{card.text}</p>}
      {view.done && <p className="m-0 type-caption text-text-tertiary">{view.done}</p>}
      {onAct && view.open && (
        <CardActions>
          <Button size="small" onClick={() => onAct(commands.acceptProposal(proposal))}>{card.confirm_label || 'Confirm'}</Button>
          <Button size="small" kind="secondary" onClick={() => onAct(commands.dismissProposal(proposal))}>Not now</Button>
        </CardActions>
      )}
    </CoachCard>
  );
}

const CARDS = { session: SessionCard, activity: ActivityCard, workout: WorkoutCard, prediction: PredictionCard, confirm: ConfirmCard };

function Sources({ sources, onOpen }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1" aria-label="What your coach read">
      <span className="type-caption text-text-tertiary">From</span>
      {sources.map((s) => {
        const Icon = SOURCE_ICON[s.kind];
        return (
          <button key={`${s.kind}-${s.id}`} type="button" onClick={() => onOpen?.(s)} disabled={!onOpen}
            className={cn('inline-flex max-w-full items-center gap-1 rounded-full type-caption text-text-secondary underline-offset-2 hover:text-text-primary hover:underline disabled:no-underline', FOCUS)}>
            <Icon size={11} className="shrink-0" aria-hidden="true" /><span className="truncate">{s.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function Steps({ steps }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className={cn('flex items-center gap-1 rounded-full type-caption text-text-tertiary hover:text-text-secondary', FOCUS)}>
        <Chevron size={12} aria-hidden="true" /> How I worked this out
      </button>
      {open && <StepList steps={steps} className="mt-1 pl-4" />}
    </div>
  );
}

/** The trail, finished or live (`working`: its last line is still going). */
export function StepList({ steps, working = false, className }) {
  return (
    <ol className={cn('m-0 list-none space-y-0.5 p-0', className)}>
      {steps.map((s, i) => (
        <li key={`${i}-${s}`} className="flex items-center gap-2 type-caption text-text-tertiary">
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', working && i === steps.length - 1 ? 'animate-pulse bg-accent-default' : 'bg-border-strong')} aria-hidden="true" />
          {s}
        </li>
      ))}
    </ol>
  );
}

export default function ReplyExtras({ body, onOpen, onAct, thread, pending }) {
  const cards = readCards(body);
  const sources = readSources(body);
  const steps = readSteps(body);
  return (
    <div className="mt-2 space-y-2">
      {cards.map((c, i) => {
        const Card = CARDS[c.kind];
        return <Card key={`${c.kind}-${c.id ?? c.proposal_id ?? i}`} card={c} onOpen={onOpen} onAct={onAct} thread={thread} pending={pending} />;
      })}
      {sources.length > 0 && <Sources sources={sources} onOpen={onOpen} />}
      {steps.length > 0 && <Steps steps={steps} />}
    </div>
  );
}
